/**
 * The two things this page opens over itself: the round sheet and the player
 * picker. Both are portalled, so both declare their own font — `.App` is where
 * this app's font stack lives, and `document.body` is outside it, which renders
 * an un-declared portal in Times.
 */
import React from 'react';
import { createPortal } from 'react-dom';

import { Avatar } from './primitives';
import { roundDescription, roundKey, roundTitle } from './format';

/** Upcoming rounds. Picking one closes the sheet. */
export const RoundSheet = ({ rounds, selected, onPick, onClose }) =>
  createPortal(
    <>
      <button type="button" className="pr-scrim" aria-label="Close"
              onClick={onClose} />
      <div className="pr-sheet pr-page" role="dialog" aria-modal="true">
        <div className="pr-sheet-handle" />
        <div className="pr-sheet-kind">Choose a round</div>

        <div className="pr-round-list">
          {rounds.map((round) => {
            const isSelected = selected
              && roundKey(round) === roundKey(selected);
            return (
              <button
                key={roundKey(round)}
                type="button"
                className={`pr-round-option${
                  isSelected ? ' is-selected' : ''}`}
                onClick={() => onPick(round)}
              >
                <span className="pr-round-option-main">
                  <span className="pr-round-option-title">
                    {round.scope === 'overall'
                      ? `${round.tournament_name} · Overall`
                      : roundTitle(round)}
                  </span>
                  <span className="pr-round-option-sub">
                    {round.scope === 'overall'
                      ? `All ${round.round_count} rounds added together`
                      : roundDescription(round)}
                  </span>
                </span>
                {/* A locked round is no longer LIVE — it is finished. Play
                    itself no longer closes a round; a complete card does. */}
                {round.locked ? (
                  <span className="pr-round-option-lock">DONE</span>
                ) : null}
                {isSelected ? <span className="pr-tick">✓</span> : null}
              </button>
            );
          })}
          {rounds.length === 0 ? (
            <div className="pr-empty">Nothing to predict on or watch.</div>
          ) : null}
        </div>
      </div>
    </>,
    document.body,
  );

/**
 * Pick one player for a slot.
 *
 * ONLY PLAYERS IN THIS ROUND are listed — you cannot back a man who is not
 * playing, and the server refuses it. `excluded` are the ones already on the
 * other side; they are shown greyed rather than hidden, so the list does not
 * change shape as slots fill and a member can see where somebody went.
 */
export const PlayerPickerModal = ({
  players, excluded = [], current = null, title, onPick, onClear, onClose,
}) => createPortal(
  <>
    <button type="button" className="pr-scrim" aria-label="Close"
            onClick={onClose} />
    <div className="pr-sheet pr-page" role="dialog" aria-modal="true">
      <div className="pr-sheet-handle" />
      <div className="pr-sheet-kind">{title}</div>

      <div className="pr-player-list">
        {players.map((player) => {
          const taken = excluded.includes(player.player_id);
          const chosen = current === player.player_id;
          return (
            <button
              key={player.player_id}
              type="button"
              className={`pr-player-option${chosen ? ' is-selected' : ''}`}
              disabled={taken}
              onClick={() => onPick(player.player_id)}
            >
              <Avatar name={player.name} size={38} me={chosen} />
              <span className="pr-player-option-main">
                <span className="pr-player-option-name">{player.name}</span>
                {player.handicap != null ? (
                  <span className="pr-player-option-sub">
                    Handicap {player.handicap}
                  </span>
                ) : null}
              </span>
              {taken ? <span className="pr-player-taken">On the other side</span>
                : chosen ? <span className="pr-tick">✓</span> : null}
            </button>
          );
        })}
        {players.length === 0 ? (
          <div className="pr-empty">Nobody is entered in this round yet.</div>
        ) : null}
      </div>

      {current != null && onClear ? (
        <button type="button" className="pr-outline-btn" onClick={onClear}>
          Clear this side
        </button>
      ) : null}
    </div>
  </>,
  document.body,
);
